import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import { useIdentityReveal } from '../../contexts/IdentityRevealContext';
import { useRealtimeResumes } from '../../hooks/useRealtimeResumes';
import { useRealtimeJobs } from '../../hooks/useRealtimeJobs';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { useTagConflictModal } from '../../hooks/useTagConflictModal';
import { canEditItem, isOrgAdmin, ownerLabel } from '../../utils/ownership';
import TransferOwnershipModal from '../../components/TransferOwnershipModal';
import { timeAgo } from '../../utils/timeAgo';
import { formatSkillLabel, isSkillExplicitInResume } from '../../utils/skillDisplay';
import { previewTagAddition, extractTagErrors, formatConflictMessages, MAX_TAG_LENGTH } from '../../utils/tagIntelligenceApi';
import {
  DEFAULT_RADAR_DIMENSION_WEIGHTS,
  RADAR_DIMENSION_KEYS,
  getRadarDimensionLabel,
} from '../../utils/radarDimensions';
import JobCandidateRadar from '../../components/charts/JobCandidateRadar';
import TagConflictAlert from '../../components/TagConflictAlert';
import './JobWorkspacePage.css';
import './ResumeViewPage.css';

interface EnrichmentData {
  consolidatedSkills: string[];
  criticValidationPassed: boolean | null;
  criticSummary: string;
  dimensions: Record<string, { confidence: number; findings: string[]; reasoning: string | string[]; skills: string[]; inferred_skills?: string[] }>;
  enrichmentMode: string;
  resumeText: string;
}

interface PreviewData {
  resumeId: string;
  filename: string;
  displayName?: string;
  candidateAlias?: string;
  originalFilename?: string | null;
  fileType: string;
  redactedText: string;
  originalPreviewUrl?: string | null;
  originalFileUrl?: string | null;
  inlinePreviewUrl?: string | null;
  inlinePreviewFileType?: string;
  previewStatus?: string;
  hasOriginalFile?: boolean;
  canViewOriginal?: boolean;
  status: string;
  uploadedAt: string;
}

type PreviewMode = 'redacted' | 'original';

const DIMENSION_META: Record<string, { label: string; subtitle: string }> = {
  technical_skills: { label: getRadarDimensionLabel('technical_skills', 'full'), subtitle: 'Role-specific applied capabilities evidenced through tasks, outcomes, and delivery depth.' },
  experience_level: { label: getRadarDimensionLabel('experience_level', 'full'), subtitle: 'Depth of responsibility, operating scale, and complexity handled across prior roles.' },
  domain_expertise: { label: getRadarDimensionLabel('domain_expertise', 'full'), subtitle: 'Knowledge of industry context, business models, regulations, and domain-specific workflows.' },
  problem_solving: { label: getRadarDimensionLabel('problem_solving', 'full'), subtitle: 'Evidence of analytical reasoning, troubleshooting quality, and solution architecture judgment.' },
  tech_stack: { label: getRadarDimensionLabel('tech_stack', 'full'), subtitle: 'Breadth, depth, and recency of tools, platforms, and ecosystems used in production contexts.' },
  transferable_skills: { label: getRadarDimensionLabel('transferable_skills', 'full'), subtitle: 'Portable competencies that transfer across functions, teams, and problem domains.' },
  education_certs: { label: getRadarDimensionLabel('education_certs', 'full'), subtitle: 'Formal credentials, certifications, and demonstrated continuous learning velocity.' },
  collaboration_leadership: { label: getRadarDimensionLabel('collaboration_leadership', 'full'), subtitle: 'Work style indicators: communication, collaboration quality, ownership, and leadership behaviors.' },
};

const DIMENSION_KEYS = RADAR_DIMENSION_KEYS;

const DEFAULT_DIMENSION_WEIGHTS: Record<string, number> = {
  ...DEFAULT_RADAR_DIMENSION_WEIGHTS,
};

async function buildRedactedPdfUrl(filename: string, redactedText: string): Promise<string> {
  const pdf = await PDFDocument.create();
  const font = await pdf.embedFont(StandardFonts.Courier);

  const pageWidth = 595;
  const pageHeight = 842;
  const margin = 40;
  const fontSize = 10;
  const lineHeight = 14;
  const maxWidth = pageWidth - (margin * 2);

  let page = pdf.addPage([pageWidth, pageHeight]);
  let y = pageHeight - margin;

  const writeLine = (line: string, color = rgb(0.13, 0.15, 0.19), size = fontSize) => {
    if (y < margin) {
      page = pdf.addPage([pageWidth, pageHeight]);
      y = pageHeight - margin;
    }
    page.drawText(line, { x: margin, y, size, font, color });
    y -= lineHeight;
  };

  const wrapLine = (line: string): string[] => {
    if (!line.trim()) return [''];

    const words = line.split(/\s+/);
    const wrapped: string[] = [];
    let current = '';

    for (const word of words) {
      const candidate = current ? `${current} ${word}` : word;
      if (font.widthOfTextAtSize(candidate, fontSize) <= maxWidth) {
        current = candidate;
        continue;
      }

      if (current) wrapped.push(current);

      if (font.widthOfTextAtSize(word, fontSize) <= maxWidth) {
        current = word;
        continue;
      }

      let chunk = '';
      for (const ch of word) {
        const next = `${chunk}${ch}`;
        if (font.widthOfTextAtSize(next, fontSize) <= maxWidth) {
          chunk = next;
        } else {
          if (chunk) wrapped.push(chunk);
          chunk = ch;
        }
      }
      current = chunk;
    }

    if (current) wrapped.push(current);
    return wrapped;
  };

  writeLine(`Redacted Resume Preview: ${filename}`, rgb(0.06, 0.32, 0.55), 11);
  writeLine('PII placeholders include [NAME], [EMAIL], [PHONE], [ADDRESS], [URL].', rgb(0.35, 0.35, 0.39), 9);
  y -= 6;

  for (const originalLine of redactedText.replace(/\r\n/g, '\n').split('\n')) {
    for (const line of wrapLine(originalLine)) {
      writeLine(line);
    }
  }

  const bytes = await pdf.save();
  const normalizedBytes = new Uint8Array(Array.from(bytes));
  const blob = new Blob([normalizedBytes], { type: 'application/pdf' });
  return URL.createObjectURL(blob);
}

export const ResumeViewPage: React.FC = () => {
  const navigate = useNavigate();
  const { resumeId } = useParams<{ resumeId: string }>();
  const { org, role } = useTenant();
  const { user } = useAuth();
  const { revealIdentity, revealMode } = useIdentityReveal();
  const authFetch = useAuthFetch();
  const { resumes, loading: resumesLoading } = useRealtimeResumes(org?.orgId);
  const { jobs } = useRealtimeJobs(org?.orgId);

  const [enrichment, setEnrichment] = useState<EnrichmentData | null>(null);
  const [enrichLoading, setEnrichLoading] = useState(false);
  const [tagSaving, setTagSaving] = useState(false);
  const [customTag, setCustomTag] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [previewMode, setPreviewMode] = useState<PreviewMode>('redacted');
  const [redactedPdfUrl, setRedactedPdfUrl] = useState<string | null>(null);
  const [redactedPdfLoading, setRedactedPdfLoading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const { tagConflictModal, openTagConflictModal, closeTagConflictModal } = useTagConflictModal('Conflicting tag. Remove the existing tag first.');
  const [deleting, setDeleting] = useState(false);
  const [expandedDimensions, setExpandedDimensions] = useState<Set<string>>(new Set());
  const [selectedLinkedJobId, setSelectedLinkedJobId] = useState<string>('');

  const PRESET_TAGS = [
    'H1B', 'Green Card', 'US Citizen', 'OPT', 'Need Sponsorship',
    'Will Relocate', 'No Relocation', 'Remote Only', 'On-site OK', 'Hybrid',
    'No Travel', 'Travel OK', 'Some Travel', '50% Travel',
    'Available Immediately', '2 Weeks Notice', '30 Days Notice',
  ];

  const fallbackAlias = useCallback((id: string) => `Candidate ${(id || 'unknown').slice(0, 8).toUpperCase()}`, []);
  const resume = useMemo(() => resumes.find(r => r.resumeId === resumeId), [resumes, resumeId]);

  const displayName = useMemo(() => {
    if (!resume) return 'Candidate';
    if (revealIdentity && resume.filename) return resume.filename;
    return resume.displayName || resume.candidateAlias || fallbackAlias(resume.resumeId);
  }, [resume, revealIdentity, fallbackAlias]);

  const linkedJobs = useMemo(() => {
    if (!resume) return [] as Array<{ jdId: string; title: string; dimensionWeights: Record<string, number> | undefined }>;
    return (resume.jdIds ?? [])
      .map(jdId => {
        const job = jobs.find(j => j.jdId === jdId);
        return {
          jdId,
          title: job?.title || 'Untitled',
          dimensionWeights: job?.dimensionWeights,
        };
      })
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [resume, jobs]);

  const canViewOriginal = revealMode === 'always_visible' || (revealMode === 'optional_toggle' && revealIdentity);
  const canEditResume = canEditItem(resume?.uploadedBy, user?.uid, role);
  const adminCanReassign = isOrgAdmin(role);
  const [showTransferOwnership, setShowTransferOwnership] = useState(false);
  const editLockedTitle = canEditResume
    ? undefined
    : 'Only the resume owner or an org admin can change this resume.';

  const loadEnrichment = useCallback(async () => {
    if (!resumeId) return;
    setEnrichLoading(true);
    try {
      const res = await authFetch(`/api/resumes/${resumeId}/enrichment`);
      if (res.ok) {
        const data = await res.json();
        setEnrichment(data);
      }
    } catch {
      // no-op
    } finally {
      setEnrichLoading(false);
    }
  }, [authFetch, resumeId]);

  useEffect(() => {
    loadEnrichment();
  }, [loadEnrichment]);

  useEffect(() => {
    if (linkedJobs.length === 0) {
      setSelectedLinkedJobId('');
      return;
    }

    const hasCurrent = linkedJobs.some(j => j.jdId === selectedLinkedJobId);
    if (!hasCurrent) {
      setSelectedLinkedJobId(linkedJobs[0].jdId);
    }
  }, [linkedJobs, selectedLinkedJobId]);

  const toggleResumeTag = useCallback(async (tag: string) => {
    if (!resume) return;
    const currentTags = resume.tags ?? [];
    if (!currentTags.includes(tag)) {
      const preview = await previewTagAddition(authFetch, 'resume', currentTags, tag);
      if (!preview.allowed) {
        const messages = [...preview.errors, ...formatConflictMessages(preview.conflicts)];
        openTagConflictModal(tag, messages);
        return;
      }
    }
    const newTags = currentTags.includes(tag)
      ? currentTags.filter(t => t !== tag)
      : [...currentTags, tag];
    setTagSaving(true);
    try {
      const res = await authFetch(`/api/resumes/${resume.resumeId}/tags`, {
        method: 'PUT',
        body: JSON.stringify({ tags: newTags }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        const messages = extractTagErrors(data);
        openTagConflictModal(tag, messages);
        return;
      }
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Could not update tags. Please retry.');
      setTimeout(() => setNotice(null), 2500);
    } finally {
      setTagSaving(false);
    }
  }, [authFetch, openTagConflictModal, resume]);

  const addCustomTag = useCallback(async () => {
    if (!resume) return;
    const val = customTag.trim();
    if (!val || (resume.tags ?? []).includes(val)) return;
    const preview = await previewTagAddition(authFetch, 'resume', resume.tags ?? [], val);
    if (!preview.allowed) {
      const messages = [...preview.errors, ...formatConflictMessages(preview.conflicts)];
      openTagConflictModal(val, messages);
      return;
    }
    setCustomTag('');
    setTagSaving(true);
    try {
      const res = await authFetch(`/api/resumes/${resume.resumeId}/tags`, {
        method: 'PUT',
        body: JSON.stringify({ tags: [...(resume.tags ?? []), val] }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        const messages = extractTagErrors(data);
        openTagConflictModal(val, messages);
        return;
      }
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'Could not add custom tag.');
      setTimeout(() => setNotice(null), 2500);
    } finally {
      setTagSaving(false);
    }
  }, [authFetch, customTag, openTagConflictModal, resume]);

  const releaseCandidate = useCallback(async () => {
    if (!resume) return;
    try {
      const res = await authFetch(`/api/resumes/${resume.resumeId}/release`, { method: 'POST' });
      if (!res.ok) throw new Error('release failed');
      setNotice('Candidate moved back to talent pool.');
    } catch {
      setNotice('Could not move candidate back to talent pool.');
    } finally {
      setTimeout(() => setNotice(null), 2800);
    }
  }, [authFetch, resume]);

  const deleteResume = useCallback(async () => {
    if (!resume || !canEditResume) return;
    setDeleting(true);
    try {
      const res = await authFetch(
        `/api/resumes/${resume.resumeId}?permanent=true`,
        { method: 'DELETE' }
      );
      if (!res.ok) throw new Error('delete failed');
      navigate('/talent');
    } catch {
      setNotice('Could not delete resume.');
      setTimeout(() => setNotice(null), 2800);
    } finally {
      setDeleting(false);
    }
  }, [authFetch, canEditResume, navigate, resume]);

  const loadPreview = useCallback(async () => {
    if (!resume) {
      setPreviewData(null);
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await authFetch(`/api/resumes/${resume.resumeId}/preview`);
      if (res.ok) {
        const data = await res.json();
        setPreviewMode(revealMode === 'always_visible' ? 'original' : 'redacted');
        setPreviewData(data);
      } else {
        setPreviewData(null);
      }
    } catch {
      setPreviewData(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [authFetch, resume, revealMode]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  useEffect(() => {
    if (revealMode === 'always_visible') {
      setPreviewMode('original');
      return;
    }

    if (revealMode === 'always_masked') {
      setPreviewMode('redacted');
      return;
    }

    if (!revealIdentity) {
      setPreviewMode('redacted');
    }
  }, [revealIdentity, revealMode]);

  const toggleDimension = useCallback((dim: string) => {
    setExpandedDimensions(prev => {
      const next = new Set(prev);
      if (next.has(dim)) next.delete(dim);
      else next.add(dim);
      return next;
    });
  }, []);

  const expandAllDimensions = useCallback(() => {
    if (!enrichment) return;
    setExpandedDimensions(new Set(Object.keys(enrichment.dimensions || {})));
  }, [enrichment]);

  const collapseAllDimensions = useCallback(() => {
    setExpandedDimensions(new Set());
  }, []);

  useEffect(() => {
    let disposed = false;

    if (!previewData) {
      setRedactedPdfLoading(false);
      setRedactedPdfUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
      return;
    }

    setRedactedPdfLoading(true);
    const redactedTitle = previewData.candidateAlias || previewData.displayName || previewData.filename;
    buildRedactedPdfUrl(redactedTitle, previewData.redactedText)
      .then((url) => {
        if (disposed) {
          URL.revokeObjectURL(url);
          return;
        }
        setRedactedPdfUrl(prev => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
      })
      .catch(() => {
        if (disposed) return;
        setRedactedPdfUrl(prev => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
      })
      .finally(() => {
        if (!disposed) setRedactedPdfLoading(false);
      });

    return () => {
      disposed = true;
    };
  }, [previewData]);

  if (resumesLoading) {
    return <div className="v2-page"><div className="v2-empty"><span className="v2-spinner" /> Loading resume...</div></div>;
  }

  if (!resume) {
    return (
      <div className="v2-page">
        <div className="v2-page-header">
          <h1>Resume Workspace</h1>
          <p>Candidate profile not found</p>
        </div>
        <div className="v2-card">
          <div className="v2-card-body">
            <p style={{ marginTop: 0, marginBottom: 'var(--space-3)' }}>This resume no longer exists or is outside your organization scope.</p>
            <button className="v2-btn v2-btn-primary" onClick={() => navigate('/talent')}>Return to Talent Pool</button>
          </div>
        </div>
      </div>
    );
  }

  const isPlaced = !!resume.placedInJobId;
  const selectedLinkedJob = linkedJobs.find(j => j.jdId === selectedLinkedJobId) || null;
  const selectedJobWeightsRaw = selectedLinkedJob?.dimensionWeights;
  const hasExplicitJobWeights = !!selectedJobWeightsRaw && Object.keys(selectedJobWeightsRaw).length > 0;
  const selectedJobWeights = hasExplicitJobWeights ? selectedJobWeightsRaw! : DEFAULT_DIMENSION_WEIGHTS;
  const selectedInlineScore = selectedLinkedJobId ? resume.inlineScores?.[selectedLinkedJobId] : undefined;
  const selectedScore = selectedInlineScore?.score;

  const normalizeDimensionMap = (source?: Record<string, unknown>): Record<string, number> | undefined => {
    if (!source) return undefined;

    const out: Record<string, number> = {};
    DIMENSION_KEYS.forEach((key) => {
      const raw = source[key];
      const parsed = Number(raw);
      if (Number.isFinite(parsed)) out[key] = parsed;
    });

    if (Object.keys(out).length === 0) return undefined;
    const maxVal = Math.max(...Object.values(out));
    if (maxVal <= 1) {
      Object.keys(out).forEach((k) => {
        out[k] = out[k] * 100;
      });
    }
    return out;
  };

  const selectedInlineDimensionScores = normalizeDimensionMap(selectedInlineScore?.dimensionScores as Record<string, unknown> | undefined);
  const enrichmentFallbackDimensionScores = normalizeDimensionMap(
    enrichment
      ? Object.fromEntries(DIMENSION_KEYS.map(key => [key, enrichment.dimensions?.[key]?.confidence]))
      : undefined,
  );
  const selectedDimensionScores = selectedInlineDimensionScores || enrichmentFallbackDimensionScores;
  const usingEnrichmentFallback = !selectedInlineDimensionScores && !!enrichmentFallbackDimensionScores;
  const normalizedSelectedScore = typeof selectedScore === 'number'
    ? (selectedScore <= 1 ? selectedScore * 100 : selectedScore)
    : undefined;

  let dominantDimension = 'none selected';
  if (!hasExplicitJobWeights && selectedLinkedJob) {
    dominantDimension = 'balanced profile';
  } else if (selectedJobWeights && Object.keys(selectedJobWeights).length > 0) {
    const entries = Object.entries(selectedJobWeights);
    if (entries.length > 0) {
      const [maxKey] = entries.reduce((acc, curr) => (curr[1] > acc[1] ? curr : acc));
      dominantDimension = (DIMENSION_META[maxKey]?.label || maxKey).toLowerCase();
    }
  }

  const resumeText = enrichment?.resumeText || '';
  const explicitSkillBadgeStyle: React.CSSProperties = {
    fontSize: 11,
    background: 'rgba(59,130,246,0.12)',
    color: '#1d4ed8',
    border: '1px solid rgba(37,99,235,0.45)',
  };
  const inferredSkillBadgeStyle: React.CSSProperties = {
    fontSize: 11,
    background: 'rgba(34,197,94,0.10)',
    color: 'var(--color-success, #22c55e)',
    border: '1px solid var(--color-success, #22c55e)',
  };

  return (
    <div className="v2-page">
      <div className="v2-page-header" style={{ marginBottom: 'var(--space-3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
          <div>
            <button className="v2-btn v2-btn-ghost" onClick={() => navigate('/talent')} style={{ marginBottom: 'var(--space-2)' }}>
              ← Return to Talent Pool
            </button>
            <h1 style={{ marginBottom: 4 }}>Resume Workspace</h1>
            <p>{displayName}</p>
          </div>
        </div>
      </div>

      {notice && (
        <div className="v2-card" style={{ marginBottom: 'var(--space-3)' }}>
          <div className="v2-card-body" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)' }}>{notice}</div>
        </div>
      )}

      <div className="job-overview-layout resume-workspace-layout">
        <article className="job-overview-pane job-overview-pane--description">
          <div className="job-overview-pane-header">
            <span>Candidate Metadata</span>
            <h2>Tags</h2>
          </div>

          <div className="job-overview-tags-row" style={{ marginBottom: 'var(--space-3)' }}>
            {PRESET_TAGS.map(tag => {
              const active = (resume.tags ?? []).includes(tag);
              if (active) {
                return (
                  <span key={tag} className="job-overview-tag job-overview-tag--static">
                    <span className="job-overview-tag__label">{tag}</span>
                    <button
                      type="button"
                      className="job-overview-tag__remove"
                      onClick={() => toggleResumeTag(tag)}
                      aria-label={`Remove ${tag} tag`}
                      disabled={tagSaving || !canEditResume}
                      title={canEditResume ? undefined : editLockedTitle}
                    >
                      x
                    </button>
                  </span>
                );
              }

              return (
                <button
                  key={tag}
                  className="job-overview-tag"
                  style={{ cursor: canEditResume ? 'pointer' : 'not-allowed' }}
                  onClick={() => toggleResumeTag(tag)}
                  disabled={tagSaving || !canEditResume}
                  title={canEditResume ? undefined : editLockedTitle}
                >
                  <span className="job-overview-tag__label">{tag}</span>
                </button>
              );
            })}
            {(resume.tags ?? []).filter(t => !PRESET_TAGS.includes(t)).map(tag => (
              <span key={tag} className="job-overview-tag job-overview-tag--static">
                <span className="job-overview-tag__label">{tag}</span>
                <button
                  type="button"
                  className="job-overview-tag__remove"
                  onClick={() => toggleResumeTag(tag)}
                  aria-label={`Remove ${tag} tag`}
                  disabled={tagSaving || !canEditResume}
                  title={canEditResume ? undefined : editLockedTitle}
                >
                  x
                </button>
              </span>
            ))}
          </div>

          <div className="job-overview-tag-input-wrap" style={{ marginBottom: 'var(--space-4)' }}>
            <input
              className="job-overview-tag-input"
              type="text"
              placeholder={canEditResume ? 'Add custom tag' : 'Tag editing locked'}
              value={customTag}
              onChange={e => setCustomTag(e.target.value)}
              maxLength={MAX_TAG_LENGTH}
              onKeyDown={e => { if (e.key === 'Enter') addCustomTag(); }}
              disabled={tagSaving || !canEditResume}
              title={canEditResume ? undefined : editLockedTitle}
            />
            <button className="job-overview-tag-add-btn" onClick={addCustomTag} disabled={tagSaving || !customTag.trim() || !canEditResume} title={canEditResume ? undefined : editLockedTitle}>
              Add
            </button>
            {tagSaving && <span className="v2-spinner" aria-hidden="true" />}
          </div>

          <TagConflictAlert
            show={tagConflictModal.show}
            pendingTag={tagConflictModal.pendingTag}
            conflicts={tagConflictModal.conflicts}
            onClose={closeTagConflictModal}
          />

          <div className="job-overview-pane-header" style={{ marginBottom: 'var(--space-2)' }}>
            <span>Deep Analysis</span>
            <h2>8D Multidimensional Enrichment</h2>
          </div>

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 'var(--space-2)' }}>
            <span className="v2-badge" style={explicitSkillBadgeStyle}>Found in resume (light blue)</span>
            <span className="v2-badge" style={inferredSkillBadgeStyle}>AI-inferred via 8D enrichment (green)</span>
          </div>

          <div className="resume-enrichment-controls">
            <button className="v2-btn v2-btn-ghost" style={{ fontSize: 'var(--font-size-xs)' }} onClick={expandAllDimensions}>Expand all</button>
            <button className="v2-btn v2-btn-ghost" style={{ fontSize: 'var(--font-size-xs)' }} onClick={collapseAllDimensions}>Collapse all</button>
          </div>

          {enrichLoading && <div className="v2-empty"><span className="v2-spinner" /> Loading enrichment...</div>}
          {!enrichLoading && !enrichment && (
            <div style={{ color: 'var(--text-muted)' }}>No enrichment data available.</div>
          )}

          {!enrichLoading && enrichment && (
            <div className="resume-enrichment-list">
              <div style={{ marginBottom: 'var(--space-2)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {(enrichment.consolidatedSkills || []).slice(0, 16).map((skill, i) => {
                  const explicit = isSkillExplicitInResume(skill, resumeText);
                  const label = formatSkillLabel(skill);
                  return (
                    <span
                      key={`${skill}-${i}`}
                      title={explicit ? 'Found in resume' : 'AI-inferred from resume context'}
                      className="v2-badge"
                      style={explicit ? explicitSkillBadgeStyle : inferredSkillBadgeStyle}
                    >
                      {label}{!explicit ? ' ✦' : ''}
                    </span>
                  );
                })}
              </div>

              {Object.entries(enrichment.dimensions).map(([dim, data]) => {
                const meta = DIMENSION_META[dim] || { label: dim, subtitle: '' };
                const dimensionSkills = [
                  ...(data.skills || []),
                  ...((data.inferred_skills || []).filter(skill => !(data.skills || []).includes(skill))),
                ];
                const expanded = expandedDimensions.has(dim);

                return (
                  <section key={dim} className="resume-enrichment-section">
                    <button className="resume-enrichment-section__header" onClick={() => toggleDimension(dim)}>
                      <div>
                        <strong>{meta.label}</strong>
                        <span>{meta.subtitle}</span>
                      </div>
                      <span className="resume-enrichment-section__chevron">{expanded ? '▾' : '▸'}</span>
                    </button>

                    {expanded && (
                      <div className="resume-enrichment-section__body">
                        {dimensionSkills.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                            {dimensionSkills.map((s, i) => {
                              const explicit = isSkillExplicitInResume(s, resumeText);
                              const label = formatSkillLabel(s);
                              return (
                                <span
                                  key={`${s}-${i}`}
                                  title={explicit ? 'Found in resume' : 'AI-inferred from resume context'}
                                  className="v2-badge"
                                  style={explicit ? explicitSkillBadgeStyle : inferredSkillBadgeStyle}
                                >
                                  {label}{!explicit ? ' ✦' : ''}
                                </span>
                              );
                            })}
                          </div>
                        )}

                        {data.findings && data.findings.length > 0 && (
                          <div>
                            {data.findings.map((f, fi) => (
                              <div key={fi} style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.5, display: 'flex', gap: 4 }}>
                                <span style={{ opacity: 0.5 }}>•</span><span>{f}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </article>

        <section className="job-overview-right">
          <div className="job-overview-right-top">
            <article className="job-overview-pane job-overview-pane--radar">
              <div className="job-overview-pane-header">
                <span>Fit Modeling</span>
                <h2>Resume-Job Fit Radar</h2>
              </div>

              {linkedJobs.length > 0 ? (
                <>
                  <div className="job-overview-radar-wrap">
                    <JobCandidateRadar
                      size={320}
                      showLabels
                      dimensionWeights={selectedJobWeights || {}}
                      dimensionScores={selectedDimensionScores}
                    />
                  </div>

                  <div className="resume-radar-legend" aria-label="Radar legend">
                    <div className="resume-radar-legend__item">
                      <span className="resume-radar-legend__swatch resume-radar-legend__swatch--job" />
                      <span>Job profile</span>
                    </div>
                    <div className="resume-radar-legend__item">
                      <span className="resume-radar-legend__swatch resume-radar-legend__swatch--candidate" />
                      <span>Resume profile</span>
                    </div>
                  </div>

                  <p className="job-overview-radar-note">
                    Diagnostic: strongest job emphasis on {dominantDimension}. {typeof normalizedSelectedScore === 'number' ? `Current fit score: ${Math.round(normalizedSelectedScore)}%.` : 'Fit score is pending.'}
                    {usingEnrichmentFallback ? ' Resume profile currently uses enrichment-derived confidence values.' : ''}
                  </p>
                </>
              ) : (
                <div className="v2-empty" style={{ minHeight: 180 }}>
                  <p style={{ margin: 0, color: 'var(--text-muted)' }}>No linked jobs yet. Open a job workspace to link this resume and view fit radar.</p>
                </div>
              )}
            </article>

            <aside className="job-overview-pane job-overview-pane--actions">
              <div className="job-overview-pane-header">
                <span>Management</span>
                <h2>Resume Actions</h2>
              </div>

              <div className="resume-action-job-controls">
                <label htmlFor="resume-linked-job-select">Selected Job</label>
                <div className="resume-action-job-controls__row">
                  <select
                    id="resume-linked-job-select"
                    value={selectedLinkedJobId}
                    onChange={e => setSelectedLinkedJobId(e.target.value)}
                    disabled={linkedJobs.length === 0}
                  >
                    {linkedJobs.length === 0 ? (
                      <option value="">No linked jobs</option>
                    ) : (
                      linkedJobs.map(job => (
                        <option key={job.jdId} value={job.jdId}>{job.title}</option>
                      ))
                    )}
                  </select>
                </div>

                <button
                  className="v2-btn v2-btn-ghost"
                  disabled={!selectedLinkedJobId}
                  onClick={() => {
                    if (!selectedLinkedJobId) return;
                    navigate(`/jobs/${selectedLinkedJobId}`);
                  }}
                >
                  Go to Selected Job
                </button>
              </div>

              <div className="job-overview-action-stack">
                {isPlaced && (
                  <button className="v2-btn v2-btn-primary" onClick={releaseCandidate}>
                    Bring Back to Pool
                  </button>
                )}
                {adminCanReassign && (
                  <button
                    className="v2-btn v2-btn-ghost"
                    onClick={() => setShowTransferOwnership(true)}
                    title="Reassign ownership of this resume to another member"
                  >
                    Transfer ownership
                  </button>
                )}
                {canEditResume ? (
                  <button className="v2-btn v2-btn-ghost" onClick={deleteResume} disabled={deleting}>
                    {deleting ? 'Deleting...' : 'Delete Resume'}
                  </button>
                ) : (
                  <button
                    className="v2-btn v2-btn-ghost"
                    disabled
                    title={editLockedTitle}
                  >
                    Delete Resume
                  </button>
                )}
              </div>

              <div className="job-overview-meta-list">
                <div><span>Status</span><strong>{resume.status === 'indexed' ? 'Ready' : resume.status}</strong></div>
                <div><span>Uploaded</span><strong>{resume.uploadedAt ? timeAgo(resume.uploadedAt).label : '—'}</strong></div>
                <div
                  title={`Owner: ${ownerLabel(resume.uploadedBy, org?.members, user?.uid)}`}
                >
                  <span>Owner</span>
                  <strong
                    style={{
                      maxWidth: 160,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'inline-block',
                    }}
                  >
                    {ownerLabel(resume.uploadedBy, org?.members, user?.uid)}
                  </strong>
                </div>
                <div><span>Lifecycle</span><strong>{isPlaced ? 'Placed' : 'Active'}</strong></div>
                <div><span>Linked Jobs</span><strong>{resume.jdIds?.length ?? 0}</strong></div>
              </div>
            </aside>
          </div>

          <section className="job-overview-pane job-overview-pane--weights resume-preview-pane">
            <div className="job-overview-pane-header">
              <span>Document View</span>
              <h2>Resume Preview</h2>
            </div>

            {revealMode === 'optional_toggle' && revealIdentity && previewData?.canViewOriginal && (
              <div style={{ display: 'inline-flex', gap: 6, padding: 4, borderRadius: 'var(--radius-md)', background: 'var(--bg-secondary)', marginBottom: 'var(--space-3)' }}>
                <button
                  className="v2-btn"
                  style={{ padding: '6px 10px', fontSize: 'var(--font-size-xs)', background: previewMode === 'redacted' ? 'var(--color-primary)' : 'transparent', color: previewMode === 'redacted' ? '#fff' : 'var(--text-secondary)' }}
                  onClick={() => setPreviewMode('redacted')}
                >
                  Masked
                </button>
                <button
                  className="v2-btn"
                  style={{ padding: '6px 10px', fontSize: 'var(--font-size-xs)', background: previewMode === 'original' ? 'var(--color-primary)' : 'transparent', color: previewMode === 'original' ? '#fff' : 'var(--text-secondary)' }}
                  onClick={() => setPreviewMode('original')}
                >
                  Original
                </button>
              </div>
            )}

            {previewLoading && (
              <div className="v2-empty"><span className="v2-spinner" /> Loading resume preview...</div>
            )}

            {!previewLoading && !previewData && (
              <div style={{ color: 'var(--text-muted)' }}>Preview not available for this resume.</div>
            )}

            {!previewLoading && previewData && ((
              revealMode === 'always_masked'
              || (revealMode === 'optional_toggle' && !revealIdentity)
              || (revealMode === 'optional_toggle' && previewMode === 'redacted')
              || (revealMode !== 'always_visible' && !(canViewOriginal && previewData.canViewOriginal))
            ) ? (
              <div>
                {redactedPdfLoading ? (
                  <div className="v2-empty"><span className="v2-spinner" /> Building redacted PDF...</div>
                ) : redactedPdfUrl ? (
                  <iframe
                    src={redactedPdfUrl}
                    title="Redacted Resume PDF"
                    style={{ width: '100%', height: 620, border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', background: '#fff' }}
                  />
                ) : (
                  <div style={{
                    padding: 'var(--space-3)', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)',
                    fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace',
                    fontSize: 'var(--font-size-xs)', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    maxHeight: 600, overflowY: 'auto', color: 'var(--text-secondary)',
                  }}>
                    {previewData.redactedText}
                  </div>
                )}
              </div>
            ) : (
              <div>
                {(() => {
                  const inlinePreviewUrl = previewData.inlinePreviewUrl || previewData.originalPreviewUrl || null;
                  const inlinePreviewFileType = previewData.inlinePreviewFileType || previewData.fileType;
                  const originalFileUrl = previewData.originalFileUrl || previewData.originalPreviewUrl || null;

                  if (!previewData.canViewOriginal) {
                    return <div className="v2-empty">Original file is not available for this resume.</div>;
                  }

                  if (inlinePreviewFileType === 'pdf' && inlinePreviewUrl) {
                    return (
                      <iframe
                        src={inlinePreviewUrl}
                        title="Original Resume PDF"
                        style={{ width: '100%', height: 620, border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', background: '#fff' }}
                      />
                    );
                  }

                  if (previewData.previewStatus === 'processing') {
                    return (
                      <div className="v2-empty"><span className="v2-spinner" /> Generating PDF preview for this DOCX...</div>
                    );
                  }

                  if (inlinePreviewUrl || previewData.hasOriginalFile) {
                    return (
                      <div style={{ padding: 'var(--space-3)', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', color: 'var(--text-secondary)' }}>
                        <p style={{ marginTop: 0 }}>Original file is available but inline preview works best for PDF files.</p>
                        {originalFileUrl && (
                          <a
                            href={originalFileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="v2-btn v2-btn-ghost"
                            style={{ display: 'inline-flex', marginBottom: 'var(--space-2)' }}
                          >
                            Download Original
                          </a>
                        )}
                      </div>
                    );
                  }

                  return <div className="v2-empty">Original file is not available.</div>;
                })()}
              </div>
            ))}
          </section>
        </section>
      </div>

      {resume && (
        <TransferOwnershipModal
          open={showTransferOwnership}
          kind="resume"
          itemId={resume.resumeId}
          itemLabel={displayName || resume.filename || 'Untitled resume'}
          currentOwnerUid={resume.uploadedBy}
          onClose={() => setShowTransferOwnership(false)}
          onTransferred={() => {
            setNotice('Ownership transferred. Realtime sync will update the page shortly.');
          }}
        />
      )}

    </div>
  );
};
